import { Button, Input, Prompt, Select, Text, toast } from "@medusajs/ui"
import { countryOptions } from "@/lib/geo"
import { useState } from "react"
import { Link } from "react-router-dom"
import { Field } from "@/components/form"
import { EmptyState, ErrorState, PageHeader, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { describeFilters, useDeleteSegment, usePreviewSegment, useSaveSegment, useSegments, type Segment, type SegmentFilters } from "@/lib/segments"

const num = (v: string) => (v.trim() === "" ? undefined : Math.max(0, Number(v)))
const Num = ({ label, value, onChange, step = 1 }: { label: string; value: number | undefined; onChange: (v: number | undefined) => void; step?: number }) => (
  <Field label={label}><Input type="number" min={0} step={step} value={value ?? ""} onChange={(e) => onChange(num(e.target.value))} /></Field>
)

/** Customers → Segments: saved filters with a live member count. */
export function SegmentsPage() {
  const { data, error, isLoading } = useSegments()
  const can = useCan()
  const [editing, setEditing] = useState<Segment | "new" | null>(null)
  const [deleting, setDeleting] = useState<Segment | null>(null)
  const del = useDeleteSegment()
  if (error) return <ErrorState error={error} />
  return (
    <>
      <PageHeader back="/customers" title="Segments" actions={can("customers:write") && <Button size="small" onClick={() => setEditing("new")}>Create segment</Button>} />
      <Section>
        {isLoading ? (
          <Text size="small" className="p-4">Loading…</Text>
        ) : !data?.segments.length ? (
          <EmptyState title="No segments yet" body="Create one, for example “Subscribed customers who ordered in the last 90 days”." />
        ) : (
          <ul className="divide-y divide-ui-border-base">
            {data.segments.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <Link to={`/customers?segment=${s.id}`} className="text-[13px] font-medium hover:underline">{s.name}</Link>
                  <Text size="xsmall" className="text-ui-fg-subtle">{describeFilters(s.filters).join(" · ")}</Text>
                </div>
                <Text size="small" className="shrink-0">{s.count ?? 0} customer{s.count === 1 ? "" : "s"}</Text>
                {can("customers:write") && (
                  <div className="flex gap-2">
                    <Button size="small" variant="secondary" onClick={() => setEditing(s)}>Edit</Button>
                    <Button size="small" variant="transparent" onClick={() => setDeleting(s)}>Delete</Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
      {editing && <SegmentEditor key={editing === "new" ? "new" : editing.id} segment={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      <Prompt open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete “{deleting?.name}”?</Prompt.Title>
            <Prompt.Description>Customers aren't affected; only the saved filter is removed.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action onClick={() => deleting && del.mutate(deleting.id, { onSuccess: () => toast.success("Segment deleted"), onError: (e) => toast.error(errorMessage(e)) })}>Delete</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  )
}

function SegmentEditor({ segment, onClose }: { segment: Segment | null; onClose: () => void }) {
  const [name, setName] = useState(segment?.name ?? "")
  const [f, setF] = useState<SegmentFilters>(segment?.filters ?? {})
  const [tags, setTags] = useState((segment?.filters.tags_any ?? []).join(", "))
  const save = useSaveSegment(segment?.id)
  const clean: SegmentFilters = (() => {
    const o: SegmentFilters = { ...f }
    const t = tags.split(",").map((x) => x.trim()).filter(Boolean)
    if (t.length) o.tags_any = t
    else delete o.tags_any
    for (const k of Object.keys(o) as (keyof SegmentFilters)[]) if (o[k] === undefined || o[k] === "") delete o[k]
    return o
  })()
  const preview = usePreviewSegment(clean)
  const set = (p: Partial<SegmentFilters>) => setF({ ...f, ...p })
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={segment ? "Edit segment" : "Create segment"}>
      <div className="my-8 w-full max-w-2xl rounded-xl bg-ui-bg-base shadow-xl">
        <div className="border-b border-ui-border-base px-5 py-4 text-[15px] font-semibold">{segment ? "Edit segment" : "Create segment"}</div>
        <div className="flex flex-col gap-4 p-5">
          <Field label="Name"><Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Email marketing">
              <Select value={f.marketing ?? "any"} onValueChange={(v) => set({ marketing: v === "any" ? undefined : (v as any) })}>
                <Select.Trigger aria-label="Email marketing"><Select.Value /></Select.Trigger>
                <Select.Content>
                  <Select.Item value="any">Any</Select.Item>
                  <Select.Item value="subscribed">Subscribed</Select.Item>
                  <Select.Item value="not_subscribed">Not subscribed</Select.Item>
                </Select.Content>
              </Select>
            </Field>
            <Field label="Account">
              <Select value={f.has_account == null ? "any" : f.has_account ? "yes" : "no"} onValueChange={(v) => set({ has_account: v === "any" ? undefined : v === "yes" })}>
                <Select.Trigger aria-label="Account"><Select.Value /></Select.Trigger>
                <Select.Content>
                  <Select.Item value="any">Any</Select.Item>
                  <Select.Item value="yes">Has an account</Select.Item>
                  <Select.Item value="no">Guest</Select.Item>
                </Select.Content>
              </Select>
            </Field>
            <Field label="Tagged with any of" ><Input value={tags} placeholder="vip, wholesale" onChange={(e) => setTags(e.target.value)} /></Field>
            <Field label="Country/region">
              <Select value={f.country?.toLowerCase() || "any"} onValueChange={(v) => set({ country: v === "any" ? undefined : v })}>
                <Select.Trigger aria-label="Country/region"><Select.Value /></Select.Trigger>
                <Select.Content>
                  <Select.Item value="any">Any</Select.Item>
                  {countryOptions().map((c) => <Select.Item key={c.code} value={c.code}>{c.name}</Select.Item>)}
                </Select.Content>
              </Select>
            </Field>
            <Num label="Orders: at least" value={f.orders_min} onChange={(v) => set({ orders_min: v })} />
            <Num label="Orders: at most" value={f.orders_max} onChange={(v) => set({ orders_max: v })} />
            <Num label="Amount spent: at least (USD)" value={f.spent_min} step={0.01} onChange={(v) => set({ spent_min: v })} />
            <Num label="Amount spent: at most (USD)" value={f.spent_max} step={0.01} onChange={(v) => set({ spent_max: v })} />
            <Num label="Ordered in the last (days)" value={f.ordered_within_days} onChange={(v) => set({ ordered_within_days: v })} />
            <Num label="No order for (days)" value={f.not_ordered_for_days} onChange={(v) => set({ not_ordered_for_days: v })} />
            <Num label="Joined in the last (days)" value={f.created_within_days} onChange={(v) => set({ created_within_days: v })} />
          </div>
          <div className="rounded-lg bg-ui-bg-subtle p-3" aria-live="polite">
            <Text size="small" weight="plus">{preview.data ? `${preview.data.count} matching customer${preview.data.count === 1 ? "" : "s"}` : "Counting…"}</Text>
            <Text size="xsmall" className="text-ui-fg-subtle">{describeFilters(clean).join(" · ")}</Text>
            {preview.data?.sample.length ? <Text size="xsmall" className="mt-1 text-ui-fg-subtle">e.g. {preview.data.sample.map((c) => c.email).join(", ")}</Text> : null}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-ui-border-base px-5 py-3">
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!name.trim()} isLoading={save.isPending} onClick={() => save.mutate({ name: name.trim(), filters: clean }, { onSuccess: () => (toast.success("Segment saved"), onClose()), onError: (e) => toast.error(errorMessage(e)) })}>Save</Button>
        </div>
      </div>
    </div>
  )
}
